/**
 * DocumentEngine — PDF generation, template management, and document lifecycle.
 *
 * Uses browser-native PDF generation (printable HTML → window.print fallback)
 * and structured templates for aged care agreements, consents, and reports.
 * In production, swap the render method for a server-side pdf-lib/puppeteer pipeline.
 */

import LocalStorageService from '../storage/LocalStorageService';

const DOCUMENTS_KEY = 'mf_documents_v1';
const TEMPLATES_KEY = 'mf_document_templates_v1';

export const DOC_STATUS = {
  DRAFT: 'draft',
  GENERATED: 'generated',
  PENDING_SIGNATURE: 'pending_signature',
  SIGNED: 'signed',
  ARCHIVED: 'archived',
};

export const DOC_TYPE = {
  RESIDENT_AGREEMENT: 'resident_agreement',
  ACCOMMODATION_AGREEMENT: 'accommodation_agreement',
  FEE_SCHEDULE: 'fee_schedule',
  STATEMENT_OF_RIGHTS: 'statement_of_rights',
  CONSENT_MEDICAL: 'consent_medical',
  CONSENT_PHOTO: 'consent_photo',
  CONSENT_MY_HEALTH_RECORD: 'consent_my_health_record',
  CONSENT_DATA_SHARING: 'consent_data_sharing',
  ADMISSION_CHECKLIST: 'admission_checklist',
  CARE_PLAN: 'care_plan',
  MONTHLY_STATEMENT: 'monthly_statement',
};

function generateId() {
  return `doc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

// ── TEMPLATES ────────────────────────────────────────────────────────────

const BUILT_IN_TEMPLATES = [
  {
    id: 'tpl_resident_agreement',
    type: DOC_TYPE.RESIDENT_AGREEMENT,
    name: 'Resident Agreement',
    description: 'Comprehensive agreement between resident/representative and facility, covering care, fees, rights, and responsibilities.',
    version: '2.0',
    sections: [
      { heading: 'Parties', fields: ['residentName', 'representativeName', 'facilityName', 'facilityAddress'] },
      { heading: 'Commencement Date', fields: ['admissionDate', 'admissionType'] },
      { heading: 'Care and Services', body: 'The facility agrees to provide care and services as set out in the resident\'s individualised care plan, in accordance with the Aged Care Quality Standards.' },
      { heading: 'Accommodation', fields: ['roomNumber', 'roomType', 'wing'] },
      { heading: 'Fees and Charges', fields: ['basicDailyFee', 'meansTestedFee', 'accommodationPayment', 'extraServices'] },
      { heading: 'Rights and Responsibilities', body: 'The resident has rights under the Charter of Aged Care Rights. A copy of the Statement of Rights is attached.' },
      { heading: 'Privacy and Confidentiality', body: 'Personal information is collected and managed in accordance with the Privacy Act 1988 and the Australian Privacy Principles.' },
      { heading: 'Complaints and Feedback', body: 'The facility maintains a complaints process. Residents may also contact the Aged Care Quality and Safety Commission.' },
      { heading: 'Termination', body: 'This agreement may be terminated by either party with appropriate notice as set out in the Aged Care Act 1997.' },
    ],
    requiredSignatures: ['resident_or_representative', 'facility_manager'],
  },
  {
    id: 'tpl_accommodation_agreement',
    type: DOC_TYPE.ACCOMMODATION_AGREEMENT,
    name: 'Accommodation Agreement',
    description: 'Refundable Accommodation Deposit (RAD) / Daily Accommodation Payment (DAP) agreement.',
    version: '1.0',
    sections: [
      { heading: 'Parties', fields: ['residentName', 'facilityName'] },
      { heading: 'Room Details', fields: ['roomNumber', 'roomType'] },
      { heading: 'Maximum Accommodation Price', fields: ['maxRAD'] },
      { heading: 'Payment Method', fields: ['paymentMethod', 'radAmount', 'dapAmount', 'combinationSplit'] },
      { heading: 'Payment Terms', body: 'Payment is due within 28 days of entry, or 28 days after means assessment, whichever is later.' },
      { heading: 'Refund Conditions', body: 'RAD is refundable in accordance with the Aged Care Act 1997 provisions.' },
    ],
    requiredSignatures: ['resident_or_representative', 'finance_manager'],
  },
  {
    id: 'tpl_fee_schedule',
    type: DOC_TYPE.FEE_SCHEDULE,
    name: 'Fee Schedule',
    description: 'Detailed schedule of all fees, charges, and additional services.',
    version: '1.0',
    sections: [
      { heading: 'Basic Daily Fee', fields: ['basicDailyFee', 'effectiveDate'] },
      { heading: 'Means-Tested Care Fee', fields: ['meansTestedFee', 'meansTestStatus'] },
      { heading: 'Accommodation Payment', fields: ['accommodationPayment', 'paymentType'] },
      { heading: 'Additional Services', fields: ['extraServices'] },
      { heading: 'Fee Review', body: 'Fees are reviewed periodically in accordance with government indexation. Written notice of any fee changes will be provided.' },
    ],
    requiredSignatures: ['resident_or_representative'],
  },
  {
    id: 'tpl_statement_of_rights',
    type: DOC_TYPE.STATEMENT_OF_RIGHTS,
    name: 'Statement of Rights',
    description: 'Charter of Aged Care Rights — acknowledgement form.',
    version: '1.0',
    sections: [
      { heading: 'Charter of Aged Care Rights', body: 'I have the right to:\n1. Safe, quality care and services\n2. Be treated with dignity and respect\n3. Have my identity, culture and diversity valued\n4. Live without abuse and neglect\n5. Be informed about my care and services\n6. Access all information about myself\n7. Have control over my personal life\n8. Have complaints dealt with fairly and promptly\n9. Have independence\n10. Be consulted on decisions about my care\n11. Manage risk in my daily life\n12. Have my personal privacy respected\n13. Exercise my rights without retribution\n14. Be treated fairly regardless of payment status' },
      { heading: 'Acknowledgement', body: 'I acknowledge that I have received, read, and understand the Charter of Aged Care Rights.' },
    ],
    requiredSignatures: ['resident_or_representative', 'witness'],
  },
  {
    id: 'tpl_consent_medical',
    type: DOC_TYPE.CONSENT_MEDICAL,
    name: 'Medical Treatment Consent',
    description: 'Consent for medical examination, treatment, and procedures.',
    version: '1.0',
    sections: [
      { heading: 'Consent', body: 'I consent to medical examination, treatment, and necessary procedures as recommended by my attending medical practitioner.' },
      { heading: 'Emergency Treatment', body: 'In an emergency, I consent to any treatment deemed necessary to preserve life or prevent serious harm.' },
      { heading: 'Advance Care Directive', fields: ['hasACD', 'acdDetails'] },
    ],
    requiredSignatures: ['resident_or_representative', 'witness'],
  },
  {
    id: 'tpl_consent_photo',
    type: DOC_TYPE.CONSENT_PHOTO,
    name: 'Photography & Media Consent',
    description: 'Consent for photos, videos, and media use in care records and marketing.',
    version: '1.0',
    sections: [
      { heading: 'Consent Scope', body: 'I consent to photographs and/or video recordings being taken for the purposes indicated below.' },
      { heading: 'Purposes', fields: ['careRecords', 'familyPortal', 'facilityNewsletter', 'website', 'socialMedia'] },
      { heading: 'Withdrawal', body: 'I understand I may withdraw this consent at any time by notifying the facility in writing.' },
    ],
    requiredSignatures: ['resident_or_representative'],
  },
  {
    id: 'tpl_consent_mhr',
    type: DOC_TYPE.CONSENT_MY_HEALTH_RECORD,
    name: 'My Health Record Consent',
    description: 'Consent for facility access to and uploading of information to My Health Record.',
    version: '1.0',
    sections: [
      { heading: 'Consent', body: 'I consent to the facility accessing my My Health Record and uploading relevant clinical information.' },
      { heading: 'IHI Details', fields: ['ihiNumber'] },
    ],
    requiredSignatures: ['resident_or_representative'],
  },
  {
    id: 'tpl_consent_data_sharing',
    type: DOC_TYPE.CONSENT_DATA_SHARING,
    name: 'Data Sharing Consent',
    description: 'Consent for sharing personal information with third parties (GP, specialists, allied health, Services Australia).',
    version: '1.0',
    sections: [
      { heading: 'Data Sharing', body: 'I consent to the sharing of my personal and health information with the following parties for the purpose of coordinated care.' },
      { heading: 'Parties', fields: ['gp', 'specialists', 'alliedHealth', 'servicesAustralia', 'pharmacist'] },
      { heading: 'Duration', body: 'This consent remains in effect for the duration of my stay unless revoked in writing.' },
    ],
    requiredSignatures: ['resident_or_representative'],
  },
];

class DocumentEngine {
  constructor() {
    this._ensureTemplates();
  }

  _ensureTemplates() {
    const existing = LocalStorageService.get(TEMPLATES_KEY, []);
    if (existing.length >= BUILT_IN_TEMPLATES.length) return;
    LocalStorageService.set(TEMPLATES_KEY, BUILT_IN_TEMPLATES);
  }

  // ── Templates ─────────────────────────────────────────────────────────

  getTemplates() {
    return LocalStorageService.get(TEMPLATES_KEY, BUILT_IN_TEMPLATES);
  }

  getTemplate(templateId) {
    return this.getTemplates().find((t) => t.id === templateId) || null;
  }

  // ── Documents ─────────────────────────────────────────────────────────

  getDocuments(filters = {}) {
    let docs = LocalStorageService.get(DOCUMENTS_KEY, []);
    if (filters.residentId) docs = docs.filter((d) => d.residentId === filters.residentId);
    if (filters.facilityId) docs = docs.filter((d) => d.facilityId === filters.facilityId);
    if (filters.status) docs = docs.filter((d) => d.status === filters.status);
    if (filters.type) docs = docs.filter((d) => d.type === filters.type);
    docs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return docs;
  }

  getDocument(documentId) {
    const docs = LocalStorageService.get(DOCUMENTS_KEY, []);
    return docs.find((d) => d.id === documentId) || null;
  }

  /**
   * Generate a document from a template, populating with resident data.
   */
  generateDocument({ templateId, residentId, facilityId, residentData = {}, createdBy = 'system' }) {
    const template = this.getTemplate(templateId);
    if (!template) return { ok: false, error: 'template_not_found' };

    const doc = {
      id: generateId(),
      templateId,
      templateName: template.name,
      type: template.type,
      version: template.version,
      residentId,
      facilityId,
      status: DOC_STATUS.GENERATED,
      residentData,
      renderedSections: this._renderSections(template.sections, residentData),
      requiredSignatures: (template.requiredSignatures || []).map((role) => ({
        role,
        signedBy: null,
        signedAt: null,
        signatureData: null,
      })),
      signatures: [],
      createdAt: new Date().toISOString(),
      createdBy,
      updatedAt: new Date().toISOString(),
    };

    const docs = LocalStorageService.get(DOCUMENTS_KEY, []);
    docs.unshift(doc);
    LocalStorageService.set(DOCUMENTS_KEY, docs);

    if (process.env.NODE_ENV === 'development') {
      console.debug('[DocumentEngine] generated', { id: doc.id, type: doc.type, residentId });
    }

    return { ok: true, document: doc };
  }

  /**
   * Generate all admission documents for a resident.
   */
  generateAdmissionPack(residentId, facilityId, residentData, createdBy) {
    const admissionTemplates = [
      'tpl_resident_agreement',
      'tpl_accommodation_agreement',
      'tpl_fee_schedule',
      'tpl_statement_of_rights',
      'tpl_consent_medical',
      'tpl_consent_photo',
      'tpl_consent_mhr',
      'tpl_consent_data_sharing',
    ];

    const results = admissionTemplates.map((templateId) =>
      this.generateDocument({ templateId, residentId, facilityId, residentData, createdBy })
    );

    return {
      ok: results.every((r) => r.ok),
      documents: results.filter((r) => r.ok).map((r) => r.document),
      errors: results.filter((r) => !r.ok).map((r) => r.error),
    };
  }

  /**
   * Update document status.
   */
  updateStatus(documentId, status) {
    const docs = LocalStorageService.get(DOCUMENTS_KEY, []);
    const idx = docs.findIndex((d) => d.id === documentId);
    if (idx === -1) return { ok: false, error: 'not_found' };
    docs[idx].status = status;
    docs[idx].updatedAt = new Date().toISOString();
    LocalStorageService.set(DOCUMENTS_KEY, docs);
    return { ok: true, document: docs[idx] };
  }

  /**
   * Add a signature to a document.
   */
  addSignature(documentId, { role, signedBy, signatureData }) {
    const docs = LocalStorageService.get(DOCUMENTS_KEY, []);
    const idx = docs.findIndex((d) => d.id === documentId);
    if (idx === -1) return { ok: false, error: 'not_found' };

    const doc = docs[idx];
    const now = new Date().toISOString();

    const sig = {
      id: `sig_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      role,
      signedBy,
      signedAt: now,
      signatureData,
      verified: true,
      ipAddress: null,
    };

    doc.signatures.push(sig);

    const reqSig = doc.requiredSignatures.find((rs) => rs.role === role && !rs.signedAt);
    if (reqSig) {
      reqSig.signedBy = signedBy;
      reqSig.signedAt = now;
      reqSig.signatureData = signatureData;
    }

    const allSigned = doc.requiredSignatures.every((rs) => rs.signedAt);
    if (allSigned) {
      doc.status = DOC_STATUS.SIGNED;
    } else {
      doc.status = DOC_STATUS.PENDING_SIGNATURE;
    }

    doc.updatedAt = now;
    LocalStorageService.set(DOCUMENTS_KEY, docs);

    if (process.env.NODE_ENV === 'development') {
      console.debug('[DocumentEngine] signature added', { documentId, role, allSigned });
    }

    return { ok: true, document: doc, allSigned };
  }

  /**
   * Get signature audit trail for a document.
   */
  getSignatureAuditTrail(documentId) {
    const doc = this.getDocument(documentId);
    if (!doc) return [];
    return doc.signatures.map((s) => ({
      ...s,
      documentId,
      documentType: doc.type,
      documentVersion: doc.version,
      residentId: doc.residentId,
    }));
  }

  /**
   * Export document as printable HTML (opens in new window for PDF via browser print).
   */
  exportAsPrintableHTML(documentId) {
    const doc = this.getDocument(documentId);
    if (!doc) return null;

    const sigBlock = doc.signatures
      .map(
        (s) =>
          `<div style="margin:10px 0;padding:10px;border:1px solid #ccc;border-radius:4px;">
            <strong>${s.role}</strong>: ${s.signedBy} — ${new Date(s.signedAt).toLocaleString()}
            ${s.signatureData ? '<br/><em>[Digital signature on file]</em>' : ''}
          </div>`
      )
      .join('');

    const sectionsHtml = (doc.renderedSections || [])
      .map(
        (s) =>
          `<div style="margin-bottom:16px;">
            <h3 style="color:#1e293b;border-bottom:1px solid #e2e8f0;padding-bottom:4px;">${s.heading}</h3>
            ${s.body ? `<p style="white-space:pre-line;">${s.body}</p>` : ''}
            ${
              s.fields
                ? `<table style="width:100%;border-collapse:collapse;margin-top:8px;">
                    ${s.fields.map((f) => `<tr><td style="padding:4px 8px;font-weight:600;width:200px;border-bottom:1px solid #f1f5f9;">${f.label}</td><td style="padding:4px 8px;border-bottom:1px solid #f1f5f9;">${f.value || '—'}</td></tr>`).join('')}
                   </table>`
                : ''
            }
          </div>`
      )
      .join('');

    return `<!DOCTYPE html>
<html><head>
<title>${doc.templateName} — ${doc.residentData?.residentName || 'Resident'}</title>
<style>
  body { font-family: 'Segoe UI', system-ui, sans-serif; max-width: 800px; margin: 40px auto; padding: 0 20px; color: #334155; line-height: 1.6; }
  h1 { color: #7c3aed; font-size: 1.5rem; }
  h2 { font-size: 1.1rem; color: #475569; }
  .meta { background: #f8fafc; padding: 12px; border-radius: 8px; margin-bottom: 20px; font-size: 0.9rem; }
  @media print { body { margin: 0; padding: 20px; } }
</style>
</head><body>
<h1>${doc.templateName}</h1>
<div class="meta">
  <strong>Document ID:</strong> ${doc.id}<br/>
  <strong>Generated:</strong> ${new Date(doc.createdAt).toLocaleString()}<br/>
  <strong>Status:</strong> ${doc.status}<br/>
  <strong>Version:</strong> ${doc.version}
</div>
${sectionsHtml}
${doc.signatures.length > 0 ? `<h2>Signatures</h2>${sigBlock}` : '<h2>Signatures</h2><p><em>Awaiting signatures</em></p>'}
</body></html>`;
  }

  /**
   * Generate admission checklist as printable PDF (HTML).
   */
  exportAdmissionChecklistHTML(residentId, tasks, residentData = {}) {
    const completed = tasks.filter((t) => t.status === 'completed').length;
    const total = tasks.length;
    const percent = total ? Math.round((completed / total) * 100) : 0;

    const taskRows = tasks
      .map(
        (t) =>
          `<tr>
            <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;">
              ${t.status === 'completed' ? '☑' : '☐'}
            </td>
            <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;">${t.title}</td>
            <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;">${t.ownerRole || '—'}</td>
            <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;">${t.dueAt || '—'}</td>
            <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;font-weight:600;color:${
              t.status === 'completed' ? '#16a34a' : t.status === 'overdue' ? '#dc2626' : t.status === 'blocked' ? '#d97706' : '#475569'
            };">${t.status}</td>
            <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;">${t.completedBy || '—'}</td>
          </tr>`
      )
      .join('');

    return `<!DOCTYPE html>
<html><head>
<title>Admission Checklist — ${residentData.residentName || residentId}</title>
<style>
  body { font-family: 'Segoe UI', system-ui, sans-serif; max-width: 900px; margin: 40px auto; padding: 0 20px; color: #334155; }
  h1 { color: #7c3aed; } table { width: 100%; border-collapse: collapse; }
  th { background: #f1f5f9; text-align: left; padding: 8px; font-size: 0.85rem; text-transform: uppercase; }
  .progress { background: #e2e8f0; border-radius: 9999px; height: 12px; margin: 8px 0; }
  .progress-bar { background: #7c3aed; height: 12px; border-radius: 9999px; }
  @media print { body { margin: 0; padding: 20px; } }
</style>
</head><body>
<h1>Admission Checklist</h1>
<p><strong>Resident:</strong> ${residentData.residentName || residentId} &nbsp;|&nbsp; <strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
<p><strong>Progress:</strong> ${completed}/${total} (${percent}%)</p>
<div class="progress"><div class="progress-bar" style="width:${percent}%"></div></div>
<table>
  <thead><tr><th></th><th>Task</th><th>Owner</th><th>Due</th><th>Status</th><th>Completed By</th></tr></thead>
  <tbody>${taskRows}</tbody>
</table>
</body></html>`;
  }

  // ── Internal ──────────────────────────────────────────────────────────

  _renderSections(sections, data) {
    return sections.map((section) => {
      const rendered = { heading: section.heading };
      if (section.body) {
        rendered.body = this._interpolate(section.body, data);
      }
      if (section.fields) {
        rendered.fields = section.fields.map((fieldKey) => ({
          label: this._fieldLabel(fieldKey),
          value: data[fieldKey] || '',
        }));
      }
      return rendered;
    });
  }

  _interpolate(text, data) {
    return text.replace(/\{\{(\w+)\}\}/g, (_, key) => data[key] || `{{${key}}}`);
  }

  _fieldLabel(key) {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (s) => s.toUpperCase())
      .trim();
  }
}

const engine = new DocumentEngine();
export default engine;
